import _ from 'lodash'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import classnames from 'classnames/bind'
import { Grid } from 'semantic-ui-react'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import ListingBadge, { BADGE_MODE } from './definitions/base'
import {
  ShortSaleBadge,
  ShortSaleOpenHousesAvailableBadge,
} from './definitions/shortSale'
import RentalBadge from './definitions/rental'
import FirstLookBadge from './definitions/firstLook'
import {
  BrokerCoopAvailableBadge,
  BrokerCoopUnavailableBadge,
} from './definitions/brokerCoop'
import {
  PropertyOccupiedBadge,
  PropertyUnknownOccupancyBadge,
  PropertyVacantBadge,
} from './definitions/occupancy'
import {
  BuyerPremiumAvailableBadge,
  BuyerPremiumUnavailableBadge,
} from './definitions/buyerPremium'
import { CashOnlyBadge, FinancingAvailableBadge } from './definitions/cashOnly'
import {
  InteriorAccessAvailableBadge,
  InteriorAccessAvailableNoAgentBadge,
  InteriorAccessUnavailableBadge,
} from './definitions/interiorAccess'
import {
  ClearedForSalePostponedBadge,
  ClearedForSalePendingBadge,
  ClearedForSaleComingSoonBadge,
  ClearedForSaleYesBadge,
} from './definitions/clearedForSale'
import {
  NotConductedByADCFCLBadge,
  ConductedByADCOnlineBadge,
  ConductedByADCLiveBadge,
  NotConductedByADCLive,
} from './definitions/conductedByADC'
import AgentInformationBadge from './definitions/agentInformation'
import OpenHouseScheduledBadge from './definitions/openHouse'
import SubjectToRedemptionBadge from './definitions/redemption'
import ForeclosureContactInformationBadge from './definitions/foreclosureContact'
import TitleAndLiensBadge from './definitions/titleAndLiens'
import RemoteBidBadge from './definitions/remoteBid'
import NewlyListedBadge from './definitions/newlyListed'
import cls from './styles.scss'
import NewlyVacantBadge from './definitions/newlyVacant'
import PriceDroppedBadge from './definitions/priceDropped'
import HotBadge from './definitions/hot'
import LikelyToGoToSaleBadge from './definitions/likelyToGoToSale'
import {
  FinanceContingency,
  InspectionContigency,
} from './definitions/contingency'
import { RentbackAuction, RentbackOffer } from './definitions/rentback'
import { OptionFee } from './definitions/optionFee'
import { AdditionalTermsBadge } from './definitions/additionalTerms'
import { WinningBidDepositBadge } from './definitions/winningBidDeposit'
import EarnestMoneyDepositReoBadge from './definitions/earnestMoneyDepositReo'
import InfoPill from '../../elementals/infoPill'
import { ColorNames } from '../../../constants/colorVars'

const cx = classnames.bind(cls)

// --------------------
// Types
// --------------------
export interface Listing {
  listing_id: string
  // Add more fields as needed from ListingBadgesFields
}

export interface BadgeClass {
  shouldBeIncluded: (
    listing: Listing,
    mode: (typeof BADGE_MODE)[keyof typeof BADGE_MODE],
    featureFlags?: Record<string, unknown>
  ) => boolean
  shouldBeIncludedV2?: (
    listing: Listing,
    mode: (typeof BADGE_MODE)[keyof typeof BADGE_MODE]
  ) => boolean
  shouldBeHoisted?: boolean
  renderPriority?: number
  renderPriorityV2?: (listing: Listing) => number
  title?: string
  id?: string
  content?: React.ReactNode
  tooltipContent?: React.ReactNode
  new?: (args: { listing: Listing; eventSubscriber?: () => void }) => any
}

interface GraphQLFragmentComponent<P = any> extends React.FC<P> {
  [GRAPH_API_FRAGMENT_DEFINITION]?: string
  [GRAPH_API_FRAGMENT_REFERENCE]?: string
}

interface ListingBadgesProps {
  listing: Listing
  eventSubscriber?: () => void
  customBadges?: BadgeClass[]
  badgesPerRow?: number
  featureFlags?: Record<string, unknown>
}

interface ListingBadgesSummaryProps {
  listing: Listing
  customBadges?: BadgeClass[]
  maxBadges?: number
  className?: string
}

interface ListingBadgesTagsProps {
  listing: Listing
  customBadges?: BadgeClass[]
  maxBadges?: number
  className?: string
  isHorizontalView?: boolean
  viewingFrom?: (typeof graph.enums.CurrentTargetAudience)[keyof typeof graph.enums.CurrentTargetAudience]
  getContainerScrollWidth?: () => number | undefined
}

// --------------------
// Badge lists
// --------------------
const availableBadges: BadgeClass[] = [
  PropertyOccupiedBadge,
  PropertyUnknownOccupancyBadge,
  PropertyVacantBadge,
  ShortSaleBadge,
  ShortSaleOpenHousesAvailableBadge,
  InteriorAccessAvailableBadge,
  InteriorAccessAvailableNoAgentBadge,
  InteriorAccessUnavailableBadge,
  BuyerPremiumAvailableBadge,
  BuyerPremiumUnavailableBadge,
  BrokerCoopAvailableBadge,
  BrokerCoopUnavailableBadge,
  FinancingAvailableBadge,
  CashOnlyBadge,
  FirstLookBadge,
  OpenHouseScheduledBadge,
  SubjectToRedemptionBadge,
  RentalBadge,
  TitleAndLiensBadge,
  AgentInformationBadge,
  ConductedByADCOnlineBadge,
  ConductedByADCLiveBadge,
  NotConductedByADCFCLBadge,
  NotConductedByADCLive,
  ClearedForSalePostponedBadge,
  ClearedForSalePendingBadge,
  ClearedForSaleComingSoonBadge,
  ClearedForSaleYesBadge,
  RemoteBidBadge,
  NewlyListedBadge,
  ForeclosureContactInformationBadge,
  EarnestMoneyDepositReoBadge,
]

const availableBadgesV2: BadgeClass[] = [
  NewlyVacantBadge,
  PriceDroppedBadge,
  NewlyListedBadge,
  FirstLookBadge,
  InteriorAccessAvailableBadge,
  InteriorAccessAvailableNoAgentBadge,
  ClearedForSaleYesBadge,
  HotBadge,
  LikelyToGoToSaleBadge,
]

const customMarkupContentBadges: Record<string, BadgeClass> = {
  AgentInformationBadge,
  BrokerCoopUnavailableBadge,
  CashOnlyBadge,
  ClearedForSalePendingBadge,
  ConductedByADCOnlineBadge,
  ConductedByADCLiveBadge,
  NotConductedByADCLive,
  NotConductedByADCFCLBadge,
  ForeclosureContactInformationBadge,
  InteriorAccessAvailableBadge,
  InteriorAccessAvailableNoAgentBadge,
  PropertyOccupiedBadge,
  OpenHouseScheduledBadge,
  SubjectToRedemptionBadge,
  RentalBadge,
  ShortSaleBadge,
  ShortSaleOpenHousesAvailableBadge,
  // @ts-ignore next
  FinanceContingency,
  // @ts-ignore next
  InspectionContigency,
  // @ts-ignore next
  RentbackOffer,
  // @ts-ignore next
  RentbackAuction,
  // @ts-ignore next
  OptionFee,
  // @ts-ignore next
  AdditionalTermsBadge,
  // @ts-ignore next
  TitleAndLiensBadge,
  // @ts-ignore next
  WinningBidDepositBadge,
  // @ts-ignore next
  EarnestMoneyDepositReoBadge,
}

// --------------------
// Utility functions
// --------------------
const getBadgeContent = (
  badge: BadgeClass & { id?: string; content?: React.ReactNode },
  listing: Listing,
  eventSubscriber?: () => void
) => {
  if (badge.id && customMarkupContentBadges[badge.id]) {
    const content = new (customMarkupContentBadges[badge.id] as any)({
      listing,
      eventSubscriber,
    }).content
    return content || badge.content
  }
  return badge.content
}

const getBadgeTooltip = (
  badge: BadgeClass & { id?: string; tooltipContent?: React.ReactNode },
  listing: Listing,
  eventSubscriber?: () => void
) => {
  if (badge.id && customMarkupContentBadges[badge.id]) {
    const tooltipContent = new (customMarkupContentBadges[badge.id] as any)({
      listing,
      eventSubscriber,
    }).tooltipContent
    return tooltipContent || badge.tooltipContent
  }
  return badge.tooltipContent
}

// --------------------
// Components
// --------------------
const ListingBadges: GraphQLFragmentComponent<ListingBadgesProps> = ({
  listing,
  eventSubscriber,
  customBadges = [],
  badgesPerRow = 2,
  featureFlags = {},
}) => {
  const [hoistedBadges, applicableBadges] = _.chain(
    availableBadges.concat(customBadges)
  )
    .filter(klass =>
      klass.shouldBeIncluded(listing, BADGE_MODE.EXPANDED, featureFlags)
    )
    .sortBy('title')
    .sortBy('renderPriority')
    .partition(klass => klass.shouldBeHoisted)
    .value()

  return (
    <section data-elm-id={`listing-badges-${listing.listing_id}`}>
      {hoistedBadges.map((Badge: any, i) => (
        <Badge listing={listing} key={i} eventSubscriber={eventSubscriber} />
      ))}
      {/* @ts-ignore next */}
      <Grid columns={badgesPerRow}>
        {applicableBadges.map((Badge: any, i) => (
          <Grid.Column key={i}>
            <Badge listing={listing} eventSubscriber={eventSubscriber} />
          </Grid.Column>
        ))}
      </Grid>
    </section>
  )
}

ListingBadges[GRAPH_API_FRAGMENT_DEFINITION] = `
  fragment ListingBadgesFields on Listing {
    listing_status
    listing_configuration {
      product_type
      broker_commission_message
      buyer_premium_available
      financing_available
      is_first_look_enabled
      first_look_end_date
      interior_access_allowed
      occupancy_status
      redemption_expiration_date
      trustee_sales_channel
      is_buyers_premium_available
      is_earnest_money_deposit_available
      asset_type
    }
    listing_summary {
      is_remote_bid_enabled
      is_remote_before_and_during_auction_enabled
    }
    program_configuration {
      buyers_premium_minimum_amount
      buyers_premium_primary_amount
      buyers_premium_commission_type
      earnest_money_deposit_primary_amount
      earnest_money_deposit_minimum_amount
    }
    primary_property {
      is_currently_saved
      is_newly_listed
    }
    closing_configuration {
      earnest_money_deposit_timeline_label
    }
    open_houses {
      local_date
      start_time
      end_time
    }
    rental {
      current_rent
      lease_start_date
      lease_expiration_date
      is_security_deposit_charged
      security_deposit_amount
    }
    short_sale {
      agreement_expiration_date
    }
    event {
      event_code
      trustee_sale
    }
    parties {
      party_role
    }
    venue {
      venue_type
    }
    marketing_tags {
      tag
    }
  }
`
ListingBadges[GRAPH_API_FRAGMENT_REFERENCE] = `...ListingBadgesFields`

// Utility for summary badges
const getApplicableBadges = (
  listing: Listing,
  customBadges: BadgeClass[],
  maxBadges: number,
  priorityField: string | ((badge: BadgeClass) => any) = 'renderPriority',
  badgesList: BadgeClass[] = availableBadges,
  // @ts-ignore next
  badgeMode: BADGE_MODE = BADGE_MODE.CONDENSED,
  shouldBeIncludedFn: keyof BadgeClass = 'shouldBeIncluded'
) =>
  _.chain(badgesList.concat(customBadges))
    .filter(klass => (klass[shouldBeIncludedFn] as any)?.(listing, badgeMode))
    .sortBy(priorityField)
    .slice(0, maxBadges)

const getV2ApplicableBadges = (
  listing: Listing,
  maxBadges = 3,
  customBadges: BadgeClass[] = []
) =>
  getApplicableBadges(
    listing,
    customBadges,
    maxBadges,
    badge => badge.renderPriorityV2?.(listing),
    availableBadgesV2,
    BADGE_MODE.V2,
    'shouldBeIncludedV2'
  )

// Summary component
const ListingBadgesSummary: GraphQLFragmentComponent<
  ListingBadgesSummaryProps
> = ({ listing, customBadges = [], maxBadges = 2, className }) => {
  const applicableBadges = getApplicableBadges(listing, customBadges, maxBadges)

  return (
    <div
      data-elm-id={`listing-badges-${listing.listing_id}`}
      className={cx(className, 'badge-summary')}
    >
      {applicableBadges.value().map((Badge: any, index: number) => (
        <Badge
          listing={listing}
          mode={BADGE_MODE.CONDENSED}
          key={index}
          className={cx('badge')}
        />
      ))}
    </div>
  )
}

ListingBadgesSummary[GRAPH_API_FRAGMENT_DEFINITION] =
  ListingBadges[GRAPH_API_FRAGMENT_DEFINITION]
ListingBadgesSummary[GRAPH_API_FRAGMENT_REFERENCE] =
  ListingBadges[GRAPH_API_FRAGMENT_REFERENCE]

// Tags version
const ListingBadgesTags: GraphQLFragmentComponent<ListingBadgesTagsProps> = ({
  listing,
  maxBadges = 3,
  className,
  isHorizontalView,
  viewingFrom = graph.enums.CurrentTargetAudience.Buyer,
  getContainerScrollWidth,
}) => {
  const [hasOverflowBadges, setHasOverflowBadges] = useState(false)

  const [topBadges, bottomBadges] = useMemo(() => {
    const applicableBadges = blueprint.listing.utils
      // @ts-ignore next
      .calculatePropertyCardBadges(listing, viewingFrom)
      .map((badge: any) => ({
        title: badge.title,
        color: badge.color,
        foregroundColor: badge.foregroundColor,
        titleIcon: badge.titleIcon,
        position: badge.position,
      }))

    const partitionedBadges = _.partition(
      applicableBadges,
      badge => badge.position !== 'bottom'
    )
    return partitionedBadges.map(p => p.slice(0, maxBadges))
  }, [listing, maxBadges, viewingFrom])

  const badgesContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const checkOverflow = () => {
      if (getContainerScrollWidth) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const containerScrollWidth = getContainerScrollWidth()
        const scrollWidth = badgesContainerRef?.current?.scrollWidth
        if (containerScrollWidth && scrollWidth) {
          const isOverflowing = containerScrollWidth <= scrollWidth + 6
          const CARD_WIDTH = 350
          const isSafeToAddHiddenOneBack = containerScrollWidth > CARD_WIDTH
          setHasOverflowBadges(prev => {
            if (isOverflowing || (prev && isSafeToAddHiddenOneBack)) {
              return isOverflowing
            }
            return prev
          })
        }
      }
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)

    return () => window.removeEventListener('resize', checkOverflow)
  }, [getContainerScrollWidth])

  return (
    <>
      <div
        ref={badgesContainerRef}
        data-elm-id={`listing-badges-${listing.listing_id}`}
        className={cx(className, 'badge-pill-container', {
          'badge-pill-map': isHorizontalView,
        })}
      >
        <InfoPill.Group
          key={listing.listing_id}
          className={cx({
            'direction-column': hasOverflowBadges,
          })}
        >
          {topBadges.map(badge => (
            <InfoPill
              key={`badge-${listing.listing_id}-${badge.title}`}
              size='x-small'
              content={badge.title}
              backgroundColor={badge.color}
              foregroundColor={ColorNames.White}
              iconName={badge.titleIcon}
              iconStyle='regular'
              className={cx('badge-info-pill')}
              contentClassName={cx('badge-info-pill-content')}
            />
          ))}
        </InfoPill.Group>
      </div>
      <div
        data-elm-id={`listing-badges-${listing.listing_id}-bottom`}
        className={cx(className, 'badge-pill-container', 'bottom-container', {
          'badge-pill-map': isHorizontalView,
        })}
      >
        {/* TODO just commenting this icons because we think it'll be back */}
        <InfoPill.Group key={listing.listing_id + '-bottom'}>
          {bottomBadges.map(badge => (
            <InfoPill
              key={`badge-${listing.listing_id}-${badge.title}`}
              size='x-small'
              content={badge.title}
              backgroundColor={badge.color}
              foregroundColor={badge.foregroundColor || ColorNames.White}
              // iconName={badge.titleIcon}
              // iconStyle='regular'
              className={cx('badge-info-pill')}
              contentClassName={cx('badge-info-pill-content')}
              includeBorder
            />
          ))}
        </InfoPill.Group>
      </div>
    </>
  )
}

ListingBadgesTags[GRAPH_API_FRAGMENT_DEFINITION] =
  ListingBadges[GRAPH_API_FRAGMENT_DEFINITION]
ListingBadgesTags[GRAPH_API_FRAGMENT_REFERENCE] =
  ListingBadges[GRAPH_API_FRAGMENT_REFERENCE]

// --------------------
// Exports
// --------------------
export default ListingBadges
export {
  ListingBadgesSummary,
  ListingBadge,
  ListingBadgesTags,
  getV2ApplicableBadges,
  getBadgeContent,
  getBadgeTooltip,
}
